/**
 * MetricsRetentionCohortsPage — 数据中台 · 用户留存（ADM-MTR-S015）
 *
 * cohort × 第 N 天 / 周 / 月回访的热力表，最近的 cohort 在上、首行为汇总。用户类型决定 cohort 起点：
 * 全部 / 免费按激活日分组，付费按首次付费日分组（人数列名「首次付费用户」）。每行按所选分组维度展开明细。留存率 = 同格回访人数 ÷ 同格 cohort 人数（服务端同批聚合行）。
 * 数据走 useMetricsRetentionCohorts（GET /api/v1/metrics/retention-cohorts）；应用维度跟随顶栏 App 选择器。
 */

import {
  RETENTION_COHORT_MAX_DAYS,
  type RetentionCohortGranularity,
  type RetentionCohortGroupDimension,
  type RetentionCohortUserType,
} from '@novel-admin/shared';
import { Fragment, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import type {
  RetentionCellView,
  RetentionChildView,
  RetentionCohortViewData,
  RetentionRowView,
} from '~/data/adapters/metrics-retention-cohorts-adapter';
import { useMetricsRetentionCohorts } from '~/data/queries';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { FilterButton } from '~/ui/FilterButton';
import { TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import {
  ChipRow,
  ToggleChip,
  daysBetween,
  downloadCsvFile,
  toDay,
} from './conversion-dashboard-shared';
import { MetricsDataExplanation } from './metrics-data-explanation';
import { Cal, EmptyBlock, StateGate, type StateGateMode } from './metrics-shared';

type ValueMode = 'rate' | 'count';

const DAY_MS = 86_400_000;
const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

const GRANULARITIES: { key: RetentionCohortGranularity; label: string; prefix: string }[] = [
  { key: 'day', label: '天', prefix: 'D' },
  { key: 'week', label: '周', prefix: 'W' },
  { key: 'month', label: '月', prefix: 'M' },
];

/** 各粒度的时间范围预设（个数），首个为默认；均在接口跨度上限内 */
const RANGE_PRESETS: Record<RetentionCohortGranularity, { count: number; label: string }[]> = {
  day: [
    { count: 30, label: '近 30 天' },
    { count: 7, label: '近 7 天' },
    { count: 14, label: '近 14 天' },
    { count: 90, label: '近 90 天' },
  ],
  week: [
    { count: 12, label: '近 12 周' },
    { count: 4, label: '近 4 周' },
    { count: 8, label: '近 8 周' },
    { count: 26, label: '近 26 周' },
  ],
  month: [
    { count: 6, label: '近 6 个月' },
    { count: 3, label: '近 3 个月' },
    { count: 12, label: '近 12 个月' },
  ],
};

const USER_TYPES: { key: RetentionCohortUserType; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'payer', label: '付费' },
  { key: 'free', label: '免费' },
];

const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** 时间窗（UTC，与服务端切日一致）：周从周一、月从 1 号开始，到今天为止 */
export function retentionRange(
  granularity: RetentionCohortGranularity,
  count: number,
  now: Date = new Date(),
): { from: string; to: string } {
  const todayMs = Date.parse(`${now.toISOString().slice(0, 10)}T00:00:00Z`);
  const to = isoDay(todayMs);
  if (granularity === 'day') return { from: isoDay(todayMs - (count - 1) * DAY_MS), to };
  if (granularity === 'week') {
    const mondayMs = todayMs - ((new Date(todayMs).getUTCDay() + 6) % 7) * DAY_MS;
    return { from: isoDay(mondayMs - (count - 1) * 7 * DAY_MS), to };
  }
  const today = new Date(todayMs);
  return {
    from: isoDay(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - (count - 1), 1)),
    to,
  };
}

/** 自选日期的起点对齐到周期首日（周一 / 1 号），接口要求周 / 月的 from 落在周期首日 */
export function alignCohortStart(granularity: RetentionCohortGranularity, day: string): string {
  const ms = Date.parse(`${day}T00:00:00Z`);
  if (granularity === 'week') return isoDay(ms - ((new Date(ms).getUTCDay() + 6) % 7) * DAY_MS);
  if (granularity === 'month') return `${day.slice(0, 8)}01`;
  return day;
}

const TABLE_TITLE: Record<RetentionCohortUserType, string> = {
  all: '新增用户留存',
  payer: '付费用户留存',
  free: '免费用户留存',
};

function periodHead(granularity: RetentionCohortGranularity, period: number): string {
  return `${GRANULARITIES.find((g) => g.key === granularity)?.prefix ?? 'D'}${period}留存`;
}

function rowLabel(row: RetentionRowView, granularity: RetentionCohortGranularity) {
  if (granularity === 'day')
    return {
      main: row.cohortStart,
      sub: WEEKDAYS[new Date(`${row.cohortStart}T00:00:00Z`).getUTCDay()] ?? '',
    };
  const inProgress = row.cohortComplete ? '' : ' · 进行中';
  if (granularity === 'week')
    return {
      main: `${row.cohortStart.slice(5)} ~ ${row.cohortEnd.slice(5)}`,
      sub: `${row.cohortStart.slice(0, 4)} 自然周${inProgress}`,
    };
  return { main: row.cohortStart.slice(0, 7), sub: `自然月${inProgress}` };
}

const GROUP_DIMENSIONS: {
  key: RetentionCohortGroupDimension;
  label: string;
  group: '基础' | '付费';
}[] = [
  { key: 'app', label: '应用', group: '基础' },
  { key: 'channel', label: '渠道', group: '基础' },
  { key: 'platform', label: '终端', group: '基础' },
  { key: 'language', label: '投放语言', group: '基础' },
  { key: 'book_code', label: '投放书籍 CODE', group: '基础' },
  { key: 'payer_status', label: '付费状态', group: '付费' },
];

function dimLabel(
  dimensions: RetentionChildView['dimensions'],
  apps: RetentionCohortViewData['options']['apps'] = [],
): string {
  const entries = Object.entries(dimensions);
  if (!entries.length) return '未知';
  const showDimensionNames = entries.length > 1;
  return entries
    .map(([rawDimension, value]) => {
      const dimension = rawDimension as RetentionCohortGroupDimension;
      const dimensionLabel =
        GROUP_DIMENSIONS.find((item) => item.key === dimension)?.label ??
        (dimension === 'country' ? '地区' : dimension);
      if (value === null || value === '')
        return showDimensionNames ? `${dimensionLabel}: 未知` : '未知';
      const text = String(value);
      const label =
        dimension === 'language'
          ? fmtLang(text)
          : dimension === 'country'
            ? fmtCountry(text)
            : dimension === 'app'
              ? (apps.find((app) => app.id === text)?.name ?? text)
              : dimension === 'platform'
                ? ({ IOS: 'iOS', ANDROID: 'Android', WEB: 'Web' }[text] ?? text)
                : dimension === 'payer_status'
                  ? text === 'payer'
                    ? '付费用户'
                    : '未付费用户'
                  : text;
      return showDimensionNames ? `${dimensionLabel}: ${label}` : label;
    })
    .join(' · ');
}

function cellText(cell: RetentionCellView, mode: ValueMode): string {
  if (cell.status === 'PENDING_MATURITY') return '';
  if (cell.status === 'PENDING_INSTRUMENTATION' || !cell.cohortSize) return '—';
  return mode === 'rate'
    ? `${((cell.rate ?? 0) * 100).toFixed(1)}%`
    : (cell.retained ?? 0).toLocaleString();
}

function cellTitle(cell: RetentionCellView): string {
  if (cell.status === 'PENDING_MATURITY') return '未到期：该周期还没走完';
  if (cell.status === 'PENDING_INSTRUMENTATION')
    return '无数据：早于留存数据沉淀起点，或该期地区已过期、不能按地区拆';
  if (!cell.cohortSize) return '该格没有用户';
  return `${((cell.rate ?? 0) * 100).toFixed(1)}% · ${(cell.retained ?? 0).toLocaleString()} / ${cell.cohortSize.toLocaleString()} 人`;
}

/**
 * 热力底色：按留存率在强调色与卡片底色之间混合（≥75% 封顶）。混合比上限 70%，配 --fg-strong 前景，
 * 亮暗两套主题下数字都保持可读对比度
 */
function heatBackground(rate: number): string {
  const weight = Math.round(Math.min(1, rate / 0.75) * 70);
  return `color-mix(in oklch, var(--accent) ${weight}%, var(--bg-elev))`;
}

function HeatCell({
  cell,
  mode,
  small,
}: { cell: RetentionCellView; mode: ValueMode; small?: boolean }) {
  const pending = cell.status === 'PENDING_MATURITY';
  const ready = cell.status === null && !!cell.cohortSize;
  return (
    <td style={{ padding: '3px 3px' }}>
      <div
        className="mono"
        title={cellTitle(cell)}
        style={{
          height: small ? 24 : 28,
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: small ? 11.5 : 12.5,
          color: ready ? 'var(--fg-strong)' : 'var(--fg-subtle)',
          background: ready ? heatBackground(cell.rate ?? 0) : 'transparent',
          border: ready ? 'none' : '1px dashed var(--border-strong)',
          opacity: pending ? 0.8 : 1,
        }}
      >
        {cellText(cell, mode)}
      </div>
    </td>
  );
}

function downloadCsv(data: RetentionCohortViewData, mode: ValueMode, usersHead: string): void {
  const header = ['时间', usersHead, ...data.periods.map((p) => periodHead(data.granularity, p))];
  // 未到期格导出为「未到期」，不留空：避免被当成 0
  const cells = (row: { cells: RetentionCellView[] }) =>
    row.cells.map((cell) => (cell.status === 'PENDING_MATURITY' ? '未到期' : cellText(cell, mode)));
  const childLines = (children: RetentionChildView[]) =>
    children.map((child) => [
      `  └ ${dimLabel(child.dimensions, data.options.apps)}`,
      String(child.cohortUsers),
      ...cells(child),
    ]);
  downloadCsvFile(
    `retention-cohorts-${data.granularity}-${data.userType}-${data.window.from}_${data.window.to}.csv`,
    [
      header,
      ['汇总', String(data.summary.cohortUsers), ...cells(data.summary)],
      ...childLines(data.summary.children),
      ...data.rows.flatMap((row) => [
        [rowLabel(row, data.granularity).main, String(row.cohortUsers), ...cells(row)],
        ...childLines(row.children),
      ]),
    ],
  );
}

export default function MetricsRetentionCohortsPage() {
  const appId = useAppScope((s) => s.appId);
  const [granularity, setGranularity] = useState<RetentionCohortGranularity>('day');
  const [rangeCount, setRangeCount] = useState<number>(RANGE_PRESETS.day[0]?.count ?? 30);
  // 自选日期（非空时优先于预设）：查看沉淀在表里、早于预设窗口的 cohort
  const [customRange, setCustomRange] = useState<[Date, Date] | null>(null);
  const [userType, setUserType] = useState<RetentionCohortUserType>('all');
  const [languages, setLanguages] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<RetentionCohortGroupDimension[]>(['language']);
  const [mode, setMode] = useState<ValueMode>('rate');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const presetRange = retentionRange(granularity, rangeCount);
  const from = customRange
    ? alignCohortStart(granularity, toDay(customRange[0]))
    : presetRange.from;
  const to = customRange ? toDay(customRange[1]) : presetRange.to;
  const spanDays = daysBetween(from, to).length;
  const rangeBlocked = spanDays > RETENTION_COHORT_MAX_DAYS[granularity] || spanDays === 0;
  const splitName = groupBy
    .map((dimension) => GROUP_DIMENSIONS.find((item) => item.key === dimension)?.label)
    .join('、');
  const usersHead = userType === 'payer' ? '首次付费用户' : '新增用户';

  const { data, isLoading, isFetching, isError, refetch } = useMetricsRetentionCohorts({
    granularity,
    from,
    to,
    userType,
    appIds: appId === 'all' ? [] : [appId],
    languages,
    countries,
    groupBy,
    enabled: !rangeBlocked,
  });

  const activeFilterCount = languages.length + countries.length;
  const resetFilters = () => {
    setLanguages([]);
    setCountries([]);
    setExpanded(new Set());
  };
  const changeGranularity = (next: RetentionCohortGranularity) => {
    setGranularity(next);
    setRangeCount(RANGE_PRESETS[next][0]?.count ?? 1);
    setExpanded(new Set());
  };
  // 换了时间窗 / 用户类型，行集合随之变化，旧的展开状态不再对应
  const changePreset = (count: number) => {
    setRangeCount(count);
    setCustomRange(null);
    setExpanded(new Set());
  };
  const changeCustomRange = (next: [Date, Date]) => {
    setCustomRange(next);
    setExpanded(new Set());
  };
  const changeUserType = (next: RetentionCohortUserType) => {
    setUserType(next);
    setExpanded(new Set());
  };
  const changeLanguages = (next: string[]) => {
    setLanguages(next);
    // 筛选变化会更新维度子行，清理旧展开态。
    setExpanded(new Set());
  };

  const gate: StateGateMode = rangeBlocked
    ? 'normal'
    : isLoading
      ? 'loading'
      : isError || !data
        ? 'error'
        : 'normal';
  // 已选中但不在当前候选里的值也要能显示 / 取消（候选随时间窗变化）
  const languageOptions = [...new Set([...(data?.options.languages ?? []), ...languages])];
  const countryOptions = [...new Set([...(data?.options.countries ?? []), ...countries])];

  return (
    <div className="page" data-screen-label="用户留存">
      <div className="page-header">
        <div>
          <h1 className="page-title">用户留存</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            {userType === 'payer' ? '按首次付费日分组' : '按激活日分组'} · 第 N 天 / 周 /
            月有任意埋点事件即算回访 · UTC
            <Cal
              t="口径"
              title="激活 = 首次归因安装（含自然量）；付费用户按最早一笔 Production 到账分组，D1 = 首付次日；免费 = 激活后至今未付费。回访 = 该周期内有任意埋点事件（只覆盖同意分析采集的用户，留存率为下限）。语言优先取投放书语言，其次用户最近语言；地区取 cohort 起点后 7 天内最早一条埋点的国家"
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          // 重取期间 data 仍是上一组筛选的占位数据，禁止导出以免导出与当前筛选不符
          disabled={!data || isFetching || rangeBlocked}
          onClick={() => {
            if (!data) return;
            downloadCsv(data, mode, usersHead);
            toast('已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
          }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
          <Icons.IconFilter size={13} />
          筛选
        </span>
        <FilterButton
          label="语言"
          options={languageOptions.map((v) => ({ value: v, label: fmtLang(v) }))}
          value={languages}
          onChange={changeLanguages}
        />
        <FilterButton
          label="地区"
          options={countryOptions.map((v) => ({ value: v, label: fmtCountry(v) }))}
          value={countries}
          onChange={setCountries}
        />
        {activeFilterCount > 0 && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
            <Icons.IconX size={11} />
            重置 {activeFilterCount}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isFetching && !isLoading && (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
        )}
      </div>

      <div className="card vstack" style={{ padding: '12px 14px', gap: 8 }}>
        <ChipRow label="用户">
          {USER_TYPES.map((u) => (
            <ToggleChip
              key={u.key}
              label={u.label}
              on={userType === u.key}
              onClick={() => changeUserType(u.key)}
            />
          ))}
        </ChipRow>
        <ChipRow label="粒度">
          {GRANULARITIES.map((g) => (
            <ToggleChip
              key={g.key}
              label={g.label}
              on={granularity === g.key}
              onClick={() => changeGranularity(g.key)}
            />
          ))}
        </ChipRow>
        <ChipRow label="时间">
          {RANGE_PRESETS[granularity].map((preset) => (
            <ToggleChip
              key={preset.count}
              label={preset.label}
              on={!customRange && rangeCount === preset.count}
              onClick={() => changePreset(preset.count)}
            />
          ))}
          <DateRangeButton
            // 面板只在挂载时读 value：换预设 / 粒度后重新挂载，打开时预选当前生效的窗口
            key={customRange ? 'custom' : `${from}_${to}`}
            value={customRange ?? [new Date(`${from}T00:00:00`), new Date(`${to}T00:00:00`)]}
            onChange={changeCustomRange}
            label={customRange ? undefined : '自选'}
            buttonClass={customRange ? 'chip chip-accent' : 'chip'}
          />
        </ChipRow>
        <ChipRow label="显示">
          <ToggleChip label="留存率" on={mode === 'rate'} onClick={() => setMode('rate')} />
          <ToggleChip label="留存数" on={mode === 'count'} onClick={() => setMode('count')} />
        </ChipRow>
        <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 4 }}>分组维度</div>
        {(['基础', '付费'] as const).map((group) => (
          <ChipRow key={group} label={group}>
            {GROUP_DIMENSIONS.filter((dimension) => dimension.group === group).map((dimension) => (
              <ToggleChip
                key={dimension.key}
                label={dimension.label}
                on={groupBy.includes(dimension.key)}
                onClick={() => {
                  setGroupBy((current) => {
                    if (current.includes(dimension.key))
                      return current.length === 1
                        ? current
                        : current.filter((item) => item !== dimension.key);
                    return GROUP_DIMENSIONS.map((item) => item.key).filter(
                      (item) => item === dimension.key || current.includes(item),
                    );
                  });
                  setExpanded(new Set());
                }}
              />
            ))}
          </ChipRow>
        ))}
      </div>

      <StateGate
        mode={gate}
        skeleton={<TableSkeleton rows={12} cols={11} />}
        errorNote="GET /api/v1/metrics/retention-cohorts 加载失败 · 请重试"
        onRetry={refetch}
      >
        {rangeBlocked && (
          <EmptyBlock
            title={`按${GRANULARITIES.find((g) => g.key === granularity)?.label ?? '天'}最多 ${RETENTION_COHORT_MAX_DAYS[granularity]} 天`}
            note={`当前选择了 ${spanDays} 天（起点已对齐到周期首日），请缩短日期范围`}
          />
        )}
        {!rangeBlocked && data && (
          <RetentionTable
            data={data}
            mode={mode}
            usersHead={usersHead}
            splitName={splitName}
            expanded={expanded}
            onToggle={(key) =>
              setExpanded((current) => {
                const next = new Set(current);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
            onToggleAll={(open) =>
              setExpanded(
                open ? new Set([data.summary.key, ...data.rows.map((r) => r.key)]) : new Set(),
              )
            }
          />
        )}
      </StateGate>
    </div>
  );
}

function RetentionTable({
  data,
  mode,
  usersHead,
  splitName,
  expanded,
  onToggle,
  onToggleAll,
}: {
  data: RetentionCohortViewData;
  mode: ValueMode;
  usersHead: string;
  splitName: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: (open: boolean) => void;
}) {
  const anyOpen = expanded.size > 0;
  const groupNote =
    data.userType === 'payer'
      ? '付费用户按首次付费日分组，D1 = 首次付费次日；'
      : data.userType === 'free'
        ? '免费用户按激活日分组，只含至今未付费的用户；'
        : '按激活日分组；';
  const periodNote =
    data.granularity === 'week'
      ? '周从周一开始；'
      : data.granularity === 'month'
        ? '月按自然月；'
        : '';

  const renderRow = (
    row: RetentionRowView,
    label: { main: string; sub: string },
    summary: boolean,
  ) => {
    const open = expanded.has(row.key);
    return (
      <Fragment key={row.key}>
        <tr style={summary ? { fontWeight: 600, background: 'var(--accent-soft)' } : undefined}>
          <td style={{ whiteSpace: 'nowrap' }}>
            <div className="hstack" style={{ gap: 6 }}>
              <button
                type="button"
                className="btn btn-ghost btn-icon btn-sm"
                aria-expanded={open}
                aria-label={`${open ? '收起' : '展开'} ${label.main} 的${splitName}明细`}
                onClick={() => onToggle(row.key)}
              >
                {open ? <Icons.IconChevDown size={13} /> : <Icons.IconChevRight size={13} />}
              </button>
              <div className="vstack" style={{ gap: 0 }}>
                <span className={summary ? undefined : 'mono'}>{label.main}</span>
                <span style={{ fontSize: 11, color: 'var(--fg-subtle)', fontWeight: 400 }}>
                  {label.sub}
                </span>
              </div>
            </div>
          </td>
          <td className="num mono">{row.cohortUsers.toLocaleString()}</td>
          {row.cells.map((cell) => (
            <HeatCell key={cell.period} cell={cell} mode={mode} />
          ))}
        </tr>
        {open &&
          row.children.map((child) => (
            <tr key={`${row.key}:${child.key}`} style={{ background: 'var(--bg-subtle)' }}>
              <td style={{ paddingLeft: 44, whiteSpace: 'nowrap', fontSize: 12.5 }}>
                {dimLabel(child.dimensions, data.options.apps)}
                <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--fg-subtle)' }}>
                  {row.cohortUsers
                    ? `${Math.round((child.cohortUsers / row.cohortUsers) * 100)}%`
                    : ''}
                </span>
              </td>
              <td className="num mono" style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                {child.cohortUsers.toLocaleString()}
              </td>
              {child.cells.map((cell) => (
                <HeatCell key={cell.period} cell={cell} mode={mode} small />
              ))}
            </tr>
          ))}
        {open && row.children.length === 0 && !(summary && row.childrenTruncated) && (
          <tr style={{ background: 'var(--bg-subtle)' }}>
            <td
              colSpan={data.periods.length + 2}
              style={{ paddingLeft: 44, fontSize: 12, color: 'var(--fg-muted)' }}
            >
              该行没有{splitName}明细
            </td>
          </tr>
        )}
        {open && row.childrenTruncated && (
          <tr style={{ background: 'var(--bg-subtle)' }}>
            <td
              colSpan={data.periods.length + 2}
              style={{ paddingLeft: 44, fontSize: 12, color: 'var(--fg-muted)' }}
            >
              {summary
                ? `部分 cohort 的${splitName}明细超过 100 个被截断，汇总行不合计明细（展开各 cohort 行查看）`
                : '明细过多，仅展示人数最多的前 100 个'}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div
        className="hstack"
        style={{
          padding: '12px 14px',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div className="vstack" style={{ gap: 2, minWidth: 0, flex: '1 1 360px' }}>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{TABLE_TITLE[data.userType]}</span>
            <MetricsDataExplanation report="retention-cohorts" />
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {groupNote}
            {periodNote}未到期为虚线。点击行首箭头展开{splitName}明细（父行为各{splitName}之和）。
          </div>
        </div>
        <div className="hstack" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span className="hstack" style={{ gap: 6, fontSize: 11.5, color: 'var(--fg-muted)' }}>
            0%
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 80,
                height: 10,
                borderRadius: 'var(--radius-sm)',
                background: `linear-gradient(90deg, ${heatBackground(0)}, ${heatBackground(0.75)})`,
              }}
            />
            ≥75%
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: 18,
                height: 12,
                marginLeft: 6,
                borderRadius: 'var(--radius-sm)',
                border: '1px dashed var(--border-strong)',
              }}
            />
            未到期
            <span className="mono" style={{ marginLeft: 6 }}>
              —
            </span>
            无数据
          </span>
          <button type="button" className="btn btn-sm" onClick={() => onToggleAll(!anyOpen)}>
            {anyOpen ? '全部收起' : '全部展开'}
          </button>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table
          className="tbl"
          style={{
            tableLayout: 'fixed',
            width: '100%',
            minWidth: 226 + data.periods.length * 62,
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 140, paddingLeft: 36 }}>时间</th>
              <th className="num" style={{ width: 86, whiteSpace: 'nowrap' }}>
                {usersHead}
              </th>
              {data.periods.map((period) => (
                <th key={period} style={{ width: 62, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {periodHead(data.granularity, period)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderRow(
              data.summary,
              { main: '汇总', sub: `${data.rows.length} 个 cohort · 加权平均` },
              true,
            )}
            {data.rows.map((row) => renderRow(row, rowLabel(row, data.granularity), false))}
          </tbody>
        </Table>
      </div>
      {data.rows.length === 0 && (
        <EmptyBlock title="当前筛选下没有 cohort" note="换个时间范围或放宽筛选" />
      )}
      <div
        style={{
          padding: '8px 14px',
          fontSize: 11.5,
          color: 'var(--fg-faint)',
          borderTop: '1px solid var(--border)',
        }}
      >
        汇总行 = 已到期 cohort 的加权平均（回访人数之和 ÷ 同格人数之和）。「—」=
        早于留存数据沉淀起点或该格无用户。数据截至{' '}
        <span className="mono">{data.lastCompleteDay} UTC</span>，每日 02:30 UTC 更新。
      </div>
    </div>
  );
}
